
import { FileServerProvider } from './file-server';
import { FirebaseStorageProvider } from './firebase-storage';
import { StorageProviderType } from './interfaces';
import { PublicFolderProvider } from './public-folder';
import { S3StorageProvider } from './s3-storage';
import { ScopedStorageProvider } from './scoped-storage';

import type { IStorageProvider } from './interfaces';

import { DataProviderBackend } from '@/lib/api/constants';

/**
 * Storage factory configuration
 */
export interface StorageFactoryConfig {
  defaultProvider: StorageProviderType;
}

/**
 * Storage factory class
 * This factory provides a centralized way to create and manage storage providers
 */
export class StorageFactory {
  private static instance: StorageFactory;
  private config: StorageFactoryConfig;
  private providers: Map<StorageProviderType, IStorageProvider>;

  private constructor(config: StorageFactoryConfig) {
    this.config = config;
    this.providers = new Map();

    // Initialize default providers
    this.initializeProviders();
  }

  /**
   * Get the storage factory instance (singleton)
   */
  public static getInstance(config?: StorageFactoryConfig): StorageFactory {
    if (!StorageFactory.instance) {
      StorageFactory.instance = new StorageFactory(config ?? {
        defaultProvider: StorageProviderType.FILE_SERVER,
      });
    }

    // Update config if provided
    if (config) {
      StorageFactory.instance.updateConfig(config);
    }

    return StorageFactory.instance;
  }

  /**
   * Update the factory configuration
   */
  public updateConfig(config: Partial<StorageFactoryConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Initialize the storage providers
   */
  private initializeProviders(): void {
    if (typeof window === 'undefined') {
      // Do not initialize providers on the server side
      return;
    }

    // Determine cloud storage backend based on DATA_PROVIDER
    const dataProvider = process.env.NEXT_PUBLIC_DATA_PROVIDER ?? DataProviderBackend.FIRESTORE;
    const useS3 = dataProvider === DataProviderBackend.MONGODB;

    if (useS3) {
      // Use S3 for cloud storage
      this.providers.set(StorageProviderType.S3, new S3StorageProvider());
      this.config.defaultProvider = StorageProviderType.S3;
    } else {
      // Use Firebase for cloud storage
      this.providers.set(StorageProviderType.FIREBASE, new FirebaseStorageProvider());
      this.config.defaultProvider = StorageProviderType.FIREBASE;
    }

    // Create File Server provider (always available for local dev)
    this.providers.set(
      StorageProviderType.FILE_SERVER,
      new FileServerProvider()
    );

    // Create Public Folder provider
    this.providers.set(
      StorageProviderType.PUBLIC_FOLDER,
      new PublicFolderProvider()
    );
  }

  /**
   * Get a storage provider by type
   */
  public getProvider(type?: StorageProviderType): IStorageProvider {
    const providerType = type ?? this.config.defaultProvider;
    const provider = this.providers.get(providerType);

    if (!provider) {
      throw new Error(`Storage provider "${providerType}" not found`);
    }

    return provider;
  }

  public getScopedStorageProvider(underlyingType?: StorageProviderType, rootPath?: string): IStorageProvider {
    const underlyingProvider = this.getProvider(underlyingType);
    return new ScopedStorageProvider(underlyingProvider, this.getProvider(StorageProviderType.PUBLIC_FOLDER), rootPath);
  }

  /**
   * Get the default storage provider
   */
  public getDefaultProvider(): IStorageProvider {
    return this.getProvider(this.config.defaultProvider);
  }
}
